/**
 * ReferralRoiCard — Admin Dashboard widget answering the "who should I
 * send a Christmas card to" question. Ranks referring realtors by their
 * total commission revenue, with a quarter-over-quarter delta chip on
 * top so the admin can see momentum, not just cumulative winners.
 *
 * Backend: GET /api/admin/referral-roi — reads `interactions` where
 * type='referral', groups by realtor_id, joins realtor display fields
 * (name, brokerage, photo), and buckets each referral into "this
 * calendar quarter" / "last calendar quarter" / "all time" so the
 * widget renders without any client-side date math.
 */
import React, { useEffect, useState } from "react";
import {
  Handshake,
  Loader2,
  TrendingUp,
  TrendingDown,
  Trophy,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { formatCurrency } from "@/components/outreach/utils";
import RealtorPeekModal from "@/components/realtors/RealtorPeekModal";

export default function ReferralRoiCard() {
  const { user } = useAuth();
  const currency = user?.currency === "USD" ? "USD" : "CAD";
  const [timeframe, setTimeframe] = useState("quarter");
  const [state, setState] = useState({ loading: true, data: null, err: "" });

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true }));
    (async () => {
      try {
        const { data } = await api.get("/referral-roi", {
          params: { limit: 10, timeframe },
        });
        if (!cancelled) setState({ loading: false, data, err: "" });
      } catch (e) {
        if (!cancelled) setState({ loading: false, data: null, err: formatApiError(e) });
      }
    })();
    return () => { cancelled = true; };
  }, [timeframe]);

  const PILLS = [
    { key: "month", label: "This Month" },
    { key: "last_month", label: "Last Month" },
    { key: "quarter", label: "This Quarter" },
    { key: "year", label: "Fiscal Year" },
    { key: "all", label: "All Time" },
  ];

  return (
    <section
      className="rounded-lg border border-slate-200 bg-white"
      data-testid="referral-roi-widget"
    >
      <header className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
        <Handshake className="h-4 w-4 text-rose-500" />
        <h2 className="text-base md:text-lg font-semibold text-slate-900">
          Referral partner ROI
        </h2>
        <span className="text-xs text-slate-500">
          top-earning referring realtors
        </span>
      </header>
      {/* Timeframe pills */}
      <div className="px-4 pt-3 flex flex-wrap gap-1.5" data-testid="referral-roi-timeframe-pills">
        {PILLS.map((p) => (
          <button
            key={p.key}
            onClick={() => setTimeframe(p.key)}
            className={
              "px-3 py-1 rounded-full text-xs font-medium transition-colors " +
              (timeframe === p.key
                ? "bg-slate-900 text-white"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200")
            }
            data-testid={`referral-roi-pill-${p.key}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="px-4 py-4">
        {state.loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2" data-testid="referral-roi-loading">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading referral data…
          </div>
        ) : state.err ? (
          <div className="text-sm text-rose-600" data-testid="referral-roi-error">
            Couldn&apos;t load stats: {state.err}
          </div>
        ) : (
          <Body data={state.data} currency={currency} />
        )}
      </div>
    </section>
  );
}

function Body({ data, currency }) {
  const thisP = data?.this_period || {};
  const prevP = data?.prev_period || {};
  const allTime = data?.all_time || {};
  const partners = data?.top_partners || [];
  const changeLabel = data?.change_label || "Change";
  const periodShort = data?.period_short || "this Q";
  const hasPrev = !!data?.prev_period;
  const [peekId, setPeekId] = useState(null);

  if (!allTime.revenue && !partners.length) {
    return (
      <div className="text-sm text-slate-500 space-y-2" data-testid="referral-roi-empty">
        <p>
          <strong className="text-slate-700">No referral revenue logged yet.</strong>{" "}
          The moment you log a referral interaction with a{" "}
          <em>referral fee</em> on any realtor&apos;s timeline, this widget
          starts ranking your highest-earning partners so you know exactly
          who deserves a Christmas card, a lunch, or a warm reconnect nudge.
        </p>
        <p className="text-xs">
          Log referrals from the realtor detail dialog → &quot;Log referral&quot; button.
        </p>
      </div>
    );
  }

  const deltaAbs = Number(data?.delta_abs) || 0;
  const deltaPct = data?.delta_pct;
  const trendUp = deltaAbs > 0;
  const trendFlat = deltaAbs === 0;
  const maxRev = Math.max(...partners.map((p) => p.revenue_this_p), 1);

  return (
    <div className="space-y-4">
      {/* Header stat row */}
      <div className={`grid gap-3 ${hasPrev ? "grid-cols-2 md:grid-cols-4" : "grid-cols-1 md:grid-cols-2"}`}>
        <Stat
          label={thisP.label || "This period"}
          value={formatMoney(thisP.revenue, currency)}
          hint={`${thisP.count || 0} referral${thisP.count === 1 ? "" : "s"}`}
          testid="referral-roi-this-period"
          accent="rose"
        />
        {hasPrev ? (
          <>
            <Stat
              label={prevP.label || "Previous period"}
              value={formatMoney(prevP.revenue, currency)}
              hint={`${prevP.count || 0} referral${prevP.count === 1 ? "" : "s"}`}
              testid="referral-roi-prev-period"
            />
            <div
              className="rounded-md border border-slate-200 px-3 py-2.5 bg-slate-50/40"
              data-testid="referral-roi-delta"
            >
              <div className="text-xs uppercase tracking-wide text-slate-500">
                {changeLabel}
              </div>
              <div className={
                "text-xl font-semibold tabular-nums mt-0.5 inline-flex items-center gap-1 " +
                (trendFlat
                  ? "text-slate-700"
                  : trendUp
                    ? "text-emerald-700"
                    : "text-rose-700")
              }>
                {trendFlat ? null : trendUp ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                {formatDelta(deltaAbs, currency)}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                {deltaPct == null
                  ? (trendFlat ? "no change" : "no prior baseline")
                  : `${deltaPct > 0 ? "+" : ""}${deltaPct}% vs ${prevP.label || "previous"}`}
              </div>
            </div>
          </>
        ) : null}
        <Stat
          label="All-time"
          value={formatMoney(allTime.revenue, currency)}
          hint={`${allTime.partner_count || 0} partner${allTime.partner_count === 1 ? "" : "s"} · ${allTime.count || 0} referral${allTime.count === 1 ? "" : "s"}`}
          testid="referral-roi-all-time"
          accent="emerald"
        />
      </div>

      {/* Top partners leaderboard */}
      <div className="rounded-md border border-slate-200 overflow-hidden">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <Trophy className="h-3.5 w-3.5 text-amber-500" />
          Top {partners.length} referral partner{partners.length === 1 ? "" : "s"}
        </div>
        <ol className="divide-y divide-slate-100" data-testid="referral-roi-leaderboard">
          {partners.map((p, idx) => (
            <PartnerRow
              key={p.realtor_id}
              rank={idx + 1}
              partner={p}
              currency={currency}
              barPct={Math.max(4, Math.round((p.revenue_this_p / maxRev) * 100))}
              periodShort={periodShort}
              onPeek={() => setPeekId(p.realtor_id)}
            />
          ))}
        </ol>
      </div>
      {peekId ? (
        <RealtorPeekModal
          realtorId={peekId}
          onClose={() => setPeekId(null)}
        />
      ) : null}
    </div>
  );
}

function PartnerRow({ rank, partner, barPct, currency, periodShort, onPeek }) {
  const p = partner;
  const delta = Number(p.revenue_this_p) - Number(p.revenue_prev_p);
  const upThisQ = delta > 0;
  const downThisQ = delta < 0;
  const initials = (p.name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
  return (
    <li
      className="px-3 py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 transition-colors"
      data-testid={`referral-roi-partner-${p.realtor_id}`}
      onClick={onPeek}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onPeek?.(); }}
    >
      {/* Rank medallion */}
      <div
        className={
          "shrink-0 h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold tabular-nums border " +
          (rank === 1
            ? "bg-amber-100 border-amber-300 text-amber-800"
            : rank === 2
              ? "bg-slate-200 border-slate-300 text-slate-800"
              : rank === 3
                ? "bg-orange-100 border-orange-300 text-orange-800"
                : "bg-slate-50 border-slate-200 text-slate-500")
        }
      >
        {rank}
      </div>
      {/* Avatar */}
      <div className="shrink-0 h-9 w-9 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center text-xs font-semibold text-slate-500 border border-slate-200">
        {p.photo_url ? (
          <img
            src={p.photo_url}
            alt=""
            className="h-full w-full object-cover"
            loading="lazy"
          />
        ) : (
          initials || "?"
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div
            className={
              "text-sm font-semibold truncate " +
              (p.is_deleted ? "text-slate-400 line-through" : "text-slate-900")
            }
            title={p.name}
          >
            {p.name}
          </div>
          {p.is_deleted ? (
            <span className="text-[10px] uppercase tracking-wide text-slate-400">
              archived
            </span>
          ) : null}
        </div>
        <div className="mt-1 h-1.5 bg-slate-100 rounded overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-rose-400 to-emerald-500"
            style={{ width: `${barPct}%` }}
          />
        </div>
        <div className="mt-1 text-[11px] text-slate-500 flex flex-wrap gap-x-3">
          {p.brokerage_name ? (
            <span className="truncate max-w-[14rem]" title={p.brokerage_name}>
              {p.brokerage_name}
            </span>
          ) : null}
          <span>
            <strong className="text-slate-800 tabular-nums">
              {p.count_this_p}
            </strong>{" "}
            referral{p.count_this_p === 1 ? "" : "s"} {periodShort}
          </span>
          {p.phone ? (
            <a
              href={`tel:${p.phone}`}
              className="inline-flex items-center gap-1 text-slate-500 hover:text-emerald-700"
              title={`Call ${p.name}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Phone className="h-3 w-3" /> call
            </a>
          ) : null}
          {p.email ? (
            <a
              href={`mailto:${p.email}?subject=Just%20checking%20in`}
              className="inline-flex items-center gap-1 text-slate-500 hover:text-indigo-700"
              title={`Email ${p.name}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Mail className="h-3 w-3" /> email
            </a>
          ) : null}
        </div>
      </div>
      {/* Revenue: period primary, all-time secondary */}
      <div className="shrink-0 text-right tabular-nums">
        <div className="text-sm font-bold text-emerald-700 flex items-center justify-end gap-1" data-testid={`referral-roi-total-${p.realtor_id}`}>
          {upThisQ ? (
            <TrendingUp className="h-3 w-3 text-emerald-500" />
          ) : downThisQ ? (
            <TrendingDown className="h-3 w-3 text-rose-500" />
          ) : null}
          {formatMoney(p.revenue_this_p, currency)}
          <span className="text-[11px] font-normal text-slate-400">{periodShort}</span>
        </div>
        <div className="text-[11px] text-slate-500">
          {formatMoney(p.revenue_total, currency)} · {p.count_total} referral{p.count_total === 1 ? "" : "s"} all-time
        </div>
        {p.last_referral_at ? (
          <div className="text-[11px] text-slate-400 mt-0.5">
            last: {formatDate(p.last_referral_at)}
          </div>
        ) : null}
      </div>
      <ArrowRight className="h-3.5 w-3.5 text-slate-400 shrink-0 hidden md:block group-hover:text-slate-600 transition-colors" />
    </li>
  );
}

function Stat({ label, value, hint, testid, accent }) {
  const accentBg = accent === "rose"
    ? "bg-rose-50/60 border-rose-200"
    : accent === "emerald"
      ? "bg-emerald-50/60 border-emerald-200"
      : "border-slate-200 bg-slate-50/40";
  const accentText = accent === "rose"
    ? "text-rose-800"
    : accent === "emerald"
      ? "text-emerald-800"
      : "text-slate-900";
  return (
    <div
      className={`rounded-md border px-3 py-2.5 ${accentBg}`}
      data-testid={testid}
    >
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`text-xl font-semibold tabular-nums mt-0.5 ${accentText}`}>
        {value}
      </div>
      {hint ? <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div> : null}
    </div>
  );
}

function formatMoney(n, currency = "CAD") {
  // Single source of truth — see formatCurrency in outreach/utils.js.
  return formatCurrency(n, currency);
}

function formatDelta(n, currency = "CAD") {
  const v = Number(n) || 0;
  if (v === 0) return formatCurrency(0, currency);
  const sign = v > 0 ? "+" : "-";
  return `${sign}${formatCurrency(Math.abs(v), currency)}`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch {
    return "—";
  }
}
